import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ChatScreenComponent } from '../../chat-screen/chat-screen.component';
import { ConfigService } from 'src/app/services/config.service';
import { IdentityService } from 'src/app/services/identity.service';
import { MuxAgentService } from 'src/app/services/mux-agent.service';
import { DialogService } from '../../../services/dialog.service';

/**
 * Trusts — the sibling of Team Policy (docs/plans/trusts-2026-10-06.md).
 *
 * Team Policy is what the organisation allows. This is who I trust: the
 * trusts that let other people talk to my agents. A trust is written once and
 * stays in force until I change, pause or revoke it; nothing is approved per
 * request. Each has a public line (shown in the team directory) and private
 * instructions only the agent sees. ("Trust" is the public word, in the
 * screen and the API; this file's own names still say "order".)
 *
 * Tabs: Orders (per agent, as sentences), Activity (who talked to what; a row
 * opens the transcript here, read-only and live, with Back), Usage (my agents'
 * spend by guest). A guest conversation is a channel in my store but not one I
 * work in, so it is read here and never listed in my Workspace.
 * Routes: docs/sdk/trusts-contract.md.
 */

interface Guest { username: string; channel_id: string; last_activity?: number; turns?: number; cost_usd?: number; }
interface Order {
  trust_id: string; agent_id: string; agent_name: string;
  audience: { type: 'team' | 'person'; username?: string };
  about: string; instructions: string; status: 'active' | 'paused';
  /** Channels the host unticked for this order. Everything else is shared, new channels included. */
  withheld_channels?: string[];
  created: number; guests: Guest[];
}
/** One guest conversation as GET /trusts/activity lists it. */
interface Conversation {
  guest: string; agent_id: string; channel_id: string; last_activity?: number | null; turns: number;
  new: number; answering: boolean; trust_id: string | null; trust_status: string | null;
  feedback?: { same: number; partly: number; no: number };
}
interface ActivityRow {
  guest: string; agent: string; channelId: string; last?: number | null; turns: number;
  fresh: number; answering: boolean; orderId: string | null;
  feedback: { same: number; partly: number; no: number };
}
/** The feedback form in the transcript strip. */
interface FeedbackDraft { mark: 'same' | 'partly' | 'no' | ''; text: string; aboutLatest: boolean; sending: boolean; }
/** The transcript that is open. `stored` are finished messages (stable objects, so the
 *  chat screen keeps their rows); `messages` adds the reply being written, if any. */
interface Viewing {
  row: ActivityRow; stored: any[]; messages: any[]; lastId: string | null;
  live: boolean; loaded: boolean; hasMore: boolean; error: string;
  lastReplyId: string | null; feedback: FeedbackDraft | null; feedbackNote: string;
}
interface UsageRow { guest: string; turns: number; cost: number; agents: string; }
interface Channel { id: string; name: string; agent_id?: string | null; last_activity?: number | null; folder?: string | null; }
interface Draft {
  orderId: string | null; agentId: string; agentName: string;
  audience: 'team' | 'person'; username: string;
  about: string; instructions: string;
  /** Unticked channel ids. Empty = everything is shared. */
  withheld: Set<string>;
}

/** Starting texts for an order. A host edits them; nothing here is enforced. */
const TEMPLATES: { key: string; label: string; about: string; instructions: string }[] = [
  {
    key: 'colleague', label: 'Colleague',
    about: 'Ask me about my current projects, how things work, and where to find things.',
    instructions: 'These are colleagues on my team. Fair game: what I am working on, how our systems and processes work, '
      + 'where documents and code live, status of shared work.\n'
      + 'Not for discussion: compensation, HR matters, finances, anything about other people, credentials or secrets.\n'
      + 'If asked for more, say it is outside what I have opened up.',
  },
  {
    key: 'client', label: 'Client',
    about: 'Ask me about the status of your project and what is planned next.',
    instructions: 'This is a client. Fair game: the status, schedule and scope of THEIR project only, in plain language.\n'
      + 'Not for discussion: other clients, pricing or contract terms, internal opinions, our tooling, anything not delivered yet '
      + 'unless it is on the agreed plan.\n'
      + 'Do not make commitments or promise dates. If asked for one, say I will confirm.',
  },
  {
    key: 'contractor', label: 'Contractor',
    about: 'Ask me how to get set up and how we do things on this project.',
    instructions: 'This is a contractor working on one project. Fair game: setup, conventions, how to build, test and '
      + 'deliver on that project, who to ask for what.\n'
      + 'Not for discussion: other projects, clients, finances, roadmap beyond their assignment, credentials or secrets.\n'
      + 'If asked for more, say it is outside what I have opened up.',
  },
];

@Component({
  selector: 'app-trusts-widget',
  templateUrl: './trusts-widget.component.html',
  styleUrls: [
    '../base-widget/base-widget.component.scss',
    '../team-policy-widget/team-policy-widget.component.scss',
    './trusts-widget.component.scss',
  ],
  standalone: false,
})
export class TrustsWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  private dialogs = inject(DialogService);
  public title = 'Trusts';
  public team = 'local';
  public tab: 'orders' | 'activity' | 'usage' = 'orders';
  public loading = true;
  public saving = false;
  public error = '';
  public notice = '';

  public agents: { id: string; name: string }[] = [];
  public orders: Order[] = [];
  public people: string[] = [];
  /** My channels in this team, newest activity first: what an order shares or keeps back. */
  public channels: Channel[] = [];
  /** Every conversation guests hold with my agents; refreshed while this screen is visible. */
  public conversations: Conversation[] = [];
  /** Replies I have not opened yet, across all conversations: the count on the Activity tab. */
  public newCount = 0;
  public viewing: Viewing | null = null;
  /** What no guest may read on my account: my list (one pattern per line) and the platform's fixed floor. */
  public neverRead = { text: '', saved: '', isDefault: true, floor: [] as string[] };
  private _activityTimer: any = null;
  private _viewTimer: any = null;
  private _gone = false;
  public editing: Draft | null = null;
  public readonly templates = TEMPLATES;
  public readonly aboutMax = 300;

  constructor(
    private http: HttpClient,
    private identity: IdentityService,
    private agentService: MuxAgentService,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.team = this.teamName || this.identity.me?.selectedTeamName || 'local';
    this.title = `Trusts · ${this.team}`;
    await this.reload();
    this._activityTimer = setInterval(() => void this.pollActivity(), 5000);
  }

  override ngOnDestroy(): void {
    this._gone = true;
    if (this._activityTimer) clearInterval(this._activityTimer);
    if (this._viewTimer) clearTimeout(this._viewTimer);
    super.ngOnDestroy();
  }

  private mux(path: string): string { return `${ConfigService.muxUrl}${path}`; }
  private get teamQ(): string { return `team=${encodeURIComponent(this.team)}`; }

  private fail(e: any, fallback: string): void {
    const detail = e?.error?.detail;
    this.error = typeof detail === 'string' && detail ? detail : fallback;
  }

  /** `quiet`: refresh the numbers without blanking the screen (used by the activity poll). */
  async reload(quiet = false): Promise<void> {
    if (!quiet) { this.loading = true; this.error = ''; }
    try {
      const [mine, agents, directory, channels, activity] = await Promise.all([
        lastValueFrom(this.http.get<{ trusts: Order[] }>(this.mux(`/trusts?${this.teamQ}`))),
        this.agentService.loadAgents(this.team),
        lastValueFrom(this.http.get<{ people: { username: string }[] }>(
          this.mux(`/teams/${encodeURIComponent(this.team)}/directory`))).catch(() => ({ people: [] })),
        lastValueFrom(this.http.get<{ channels: Channel[] }>(this.mux(`/trusts/channels?${this.teamQ}`)))
          .catch(() => ({ channels: [] })),
        lastValueFrom(this.http.get<{ conversations: Conversation[] }>(this.mux(`/trusts/activity?${this.teamQ}`)))
          .catch(() => ({ conversations: this.conversations })),
      ]);
      if (!quiet) await this.loadNeverRead();
      this.conversations = activity?.conversations || [];
      this.channels = channels?.channels || [];
      this.orders = mine?.trusts || [];
      this.agents = (agents || [])
        .filter((a: any) => !a.archived)
        .map((a: any) => ({ id: a.id, name: a.name || a.id }));
      // An order for an agent that is no longer in the list still shows, under its recorded name.
      for (const o of this.orders) {
        if (!this.agents.some((a) => a.id === o.agent_id)) this.agents.push({ id: o.agent_id, name: o.agent_name || o.agent_id });
      }
      this.people = (directory?.people || []).map((p) => p.username).sort();
      this.rebuildRows();
    } catch (e) {
      if (!quiet) this.fail(e, 'Could not load your trusts.');
    } finally {
      this.loading = false;
    }
  }

  // ---- orders -----------------------------------------------------------------

  ordersFor(agentId: string): Order[] {
    return this.orders.filter((o) => o.agent_id === agentId);
  }

  /** "Everyone on acme" / "Dyke" — who the order is for. */
  audienceLabel(o: Order): string {
    return o.audience.type === 'person' ? (o.audience.username || 'One person') : `Everyone on ${this.team}`;
  }

  guestSummary(o: Order): string {
    const n = o.guests.length;
    if (!n) return o.audience.type === 'person' ? 'has not asked yet' : 'nobody has asked yet';
    const last = o.guests[0];
    const who = o.audience.type === 'person' ? 'last asked' : `${n} ${n === 1 ? 'person has' : 'people have'} asked · last: ${last.username},`;
    return `${who} ${this.ago(last.last_activity)}`;
  }

  get hasOrders(): boolean { return this.orders.length > 0; }
  get allPaused(): boolean { return this.hasOrders && this.orders.every((o) => o.status === 'paused'); }

  /** One switch: pause every order, or put them all back in force. */
  async toggleAll(pause: boolean): Promise<void> {
    if (this.saving || !this.hasOrders) return;
    this.saving = true; this.error = ''; this.notice = '';
    try {
      if (pause) {
        await lastValueFrom(this.http.post(this.mux(`/trusts/pause-all?${this.teamQ}`), {}));
        this.notice = 'Paused. Nobody can send to your agents until you resume.';
      } else {
        for (const o of this.orders.filter((x) => x.status === 'paused')) {
          await lastValueFrom(this.http.patch(this.mux(`/trusts/${o.trust_id}?${this.teamQ}`), { status: 'active' }));
        }
        this.notice = 'Resumed. Your trusts are in force again.';
      }
      await this.reload();
    } catch (e) {
      this.fail(e, 'Could not change your trusts.');
    } finally {
      this.saving = false;
    }
  }

  startNew(agent: { id: string; name: string }): void {
    const hasTeamOrder = this.ordersFor(agent.id).some((o) => o.audience.type === 'team');
    this.editing = {
      orderId: null, agentId: agent.id, agentName: agent.name,
      audience: hasTeamOrder ? 'person' : 'team', username: '',
      about: '', instructions: '', withheld: new Set<string>(),
    };
    this.error = ''; this.notice = '';
  }

  startEdit(o: Order): void {
    this.editing = {
      orderId: o.trust_id, agentId: o.agent_id, agentName: o.agent_name,
      audience: o.audience.type, username: o.audience.username || '',
      about: o.about, instructions: o.instructions, withheld: new Set<string>(o.withheld_channels || []),
    };
    this.error = ''; this.notice = '';
  }

  cancelEdit(): void { this.editing = null; }

  /** People who can still be named for this agent (not already named). */
  get nameablePeople(): string[] {
    const d = this.editing;
    if (!d) return [];
    const taken = new Set(this.ordersFor(d.agentId)
      .filter((o) => o.audience.type === 'person' && o.trust_id !== d.orderId)
      .map((o) => o.audience.username));
    return this.people.filter((p) => !taken.has(p));
  }

  // ---- what no guest may read ---------------------------------------------------------

  private setNeverRead(r: any): void {
    const text = (r?.patterns || []).join('\n');
    this.neverRead = { text, saved: text, isDefault: !!r?.is_default, floor: r?.floor || [] };
  }

  private async loadNeverRead(): Promise<void> {
    try {
      this.setNeverRead(await lastValueFrom(this.http.get<any>(this.mux(`/trusts/never-read?${this.teamQ}`))));
    } catch { /* an older server has no such list; the card stays empty */ }
  }

  get neverReadChanged(): boolean { return this.neverRead.text.trim() !== this.neverRead.saved.trim(); }

  async saveNeverRead(reset = false): Promise<void> {
    if (this.saving) return;
    this.saving = true; this.error = ''; this.notice = '';
    try {
      const body = reset ? { reset: true }
        : { patterns: this.neverRead.text.split('\n').map((l) => l.trim()).filter((l) => !!l) };
      this.setNeverRead(await lastValueFrom(this.http.put<any>(this.mux(`/trusts/never-read?${this.teamQ}`), body)));
      this.notice = reset ? 'Back to the starting list.' : 'Saved. It applies from each guest\'s next message.';
    } catch (e) {
      this.fail(e, 'Could not save the list.');
    } finally {
      this.saving = false;
    }
  }

  // ---- which channels an order shares ---------------------------------------------

  /** The editor's list: this agent's channels first, then the ones another agent runs. */
  get draftChannels(): Channel[] {
    const d = this.editing;
    if (!d) return [];
    const mine = (c: Channel) => !c.agent_id || c.agent_id === d.agentId;
    return [...this.channels.filter(mine), ...this.channels.filter((c) => !mine(c))];
  }

  /** "runs with coach" for a channel another agent of mine works in; '' for this agent's own. */
  otherAgentLabel(c: Channel): string {
    const d = this.editing;
    if (!d || !c.agent_id || c.agent_id === d.agentId) return '';
    return this.agents.find((a) => a.id === c.agent_id)?.name || c.agent_id;
  }

  setShared(c: Channel, shared: boolean): void {
    if (!this.editing) return;
    if (shared) this.editing.withheld.delete(c.id); else this.editing.withheld.add(c.id);
  }

  setAllShared(shared: boolean): void {
    if (!this.editing) return;
    for (const c of this.channels) this.setShared(c, shared);
  }

  get sharedCount(): number {
    const d = this.editing;
    return d ? this.channels.filter((c) => !d.withheld.has(c.id)).length : 0;
  }

  /** "finance, ops" — the channels an order keeps back, by name, for its card. */
  withheldNames(o: Order): string {
    return (o.withheld_channels || [])
      .map((id) => this.channels.find((c) => c.id === id)?.name)
      .filter((n): n is string => !!n)
      .join(', ');
  }

  applyTemplate(key: string): void {
    const t = this.templates.find((x) => x.key === key);
    if (!t || !this.editing) return;
    this.editing.about = t.about;
    this.editing.instructions = t.instructions;
  }

  get canSave(): boolean {
    const d = this.editing;
    return !!d && !this.saving && d.about.trim().length > 0 && d.about.length <= this.aboutMax
      && (d.audience === 'team' || !!d.username);
  }

  async saveDraft(): Promise<void> {
    const d = this.editing;
    if (!d || !this.canSave) return;
    this.saving = true; this.error = '';
    try {
      if (d.orderId) {
        await lastValueFrom(this.http.patch(this.mux(`/trusts/${d.orderId}?${this.teamQ}`),
          { about: d.about.trim(), instructions: d.instructions.trim(), withheld_channels: Array.from(d.withheld) }));
      } else {
        await lastValueFrom(this.http.post(this.mux('/trusts'), {
          team: this.team, agent_id: d.agentId,
          audience: d.audience === 'team' ? { type: 'team' } : { type: 'person', username: d.username },
          about: d.about.trim(), instructions: d.instructions.trim(),
          withheld_channels: Array.from(d.withheld),
        }));
      }
      this.editing = null;
      this.notice = 'Saved. It is in force now.';
      await this.reload();
    } catch (e) {
      this.fail(e, 'Could not save the trust.');
    } finally {
      this.saving = false;
    }
  }

  async togglePause(o: Order): Promise<void> {
    if (this.saving) return;
    this.saving = true; this.error = ''; this.notice = '';
    try {
      await lastValueFrom(this.http.patch(this.mux(`/trusts/${o.trust_id}?${this.teamQ}`),
        { status: o.status === 'paused' ? 'active' : 'paused' }));
      await this.reload();
    } catch (e) {
      this.fail(e, 'Could not change the trust.');
    } finally {
      this.saving = false;
    }
  }

  async revoke(o: Order): Promise<void> {
    const who = o.audience.type === 'person' ? o.audience.username : `everyone on ${this.team}`;
    const ok = await this.dialogs.confirm(
      `End this trust? ${who} will no longer be able to talk to ${o.agent_name}. The conversations stay readable under Activity.`,
      { title: 'Revoke trust', confirmText: 'Revoke' });
    if (!ok) return;
    this.saving = true; this.error = ''; this.notice = '';
    try {
      await lastValueFrom(this.http.delete(this.mux(`/trusts/${o.trust_id}?${this.teamQ}`)));
      this.notice = 'Revoked.';
      await this.reload();
    } catch (e) {
      this.fail(e, 'Could not revoke the trust.');
    } finally {
      this.saving = false;
    }
  }

  // ---- activity + usage ---------------------------------------------------------

  // Rows are built once per load, not in getters: a getter hands *ngFor fresh
  // objects on every change detection, the row under the pointer is replaced
  // between mouse-down and mouse-up, and its click never fires.
  public activity: ActivityRow[] = [];
  public usageByGuest: UsageRow[] = [];
  public usageTotal = { turns: 0, cost: 0 };

  private rebuildRows(): void {
    this.activity = this.buildActivity();
    this.newCount = this.activity.reduce((n, r) => n + r.fresh, 0);
    this.usageByGuest = this.buildUsageByGuest();
    this.usageTotal = this.usageByGuest.reduce((t, r) => ({ turns: t.turns + r.turns, cost: t.cost + r.cost }), { turns: 0, cost: 0 });
  }

  trackActivity(_: number, r: ActivityRow): string { return r.channelId; }

  /** Every guest conversation, newest first (the server's order), including the
   *  ones whose order was revoked: the record stays readable. */
  private buildActivity(): ActivityRow[] {
    return this.conversations.map((c) => ({
      guest: c.guest, agent: this.agents.find((a) => a.id === c.agent_id)?.name || c.agent_id,
      channelId: c.channel_id, last: c.last_activity, turns: c.turns || 0,
      fresh: this.viewing?.row.channelId === c.channel_id ? 0 : (c.new || 0),
      answering: !!c.answering, orderId: c.trust_id,
      feedback: c.feedback || { same: 0, partly: 0, no: 0 },
    }));
  }

  /** While this screen is on display: who has talked to my agents since I last looked. */
  private async pollActivity(): Promise<void> {
    if (this._gone || this.loading || this.saving || document.hidden) return;
    try {
      const r = await lastValueFrom(this.http.get<{ conversations: Conversation[] }>(this.mux(`/trusts/activity?${this.teamQ}`)));
      const before = this.conversations.reduce((n, c) => n + (c.turns || 0), 0);
      this.conversations = r?.conversations || [];
      this.rebuildRows();
      // A new reply also moves the per-order summaries and the spend: refresh those quietly.
      const after = this.conversations.reduce((n, c) => n + (c.turns || 0), 0);
      if (after !== before && !this.editing) void this.reload(true);
    } catch { /* the next tick tries again */ }
  }

  // ---- one conversation, read-only and live --------------------------------------

  orderOf(row: ActivityRow): Order | null {
    return this.orders.find((o) => o.trust_id === row.orderId) || null;
  }

  /** "Everyone on acme can talk to claude" — the order this conversation runs under. */
  orderLine(row: ActivityRow): string {
    const o = this.orderOf(row);
    if (!o) return 'The trust was revoked. Nobody can add to this conversation.';
    return `${this.audienceLabel(o)} can talk to ${o.agent_name}${o.status === 'paused' ? ' (paused)' : ''}`;
  }

  openConversation(row: ActivityRow): void {
    this.closeConversation();
    this.viewing = { row, stored: [], messages: [], lastId: null, live: false, loaded: false, hasMore: false, error: '',
                     lastReplyId: null, feedback: null, feedbackNote: '' };
    row.fresh = 0;
    this.rebuildRows();
    void this.pollConversation();
  }

  closeConversation(): void {
    if (this._viewTimer) { clearTimeout(this._viewTimer); this._viewTimer = null; }
    this.viewing = null;
  }

  /** Read the order's instructions while the transcript is fresh in mind: Back, then its editor. */
  editOrderFrom(row: ActivityRow): void {
    const o = this.orderOf(row);
    if (!o) return;
    this.closeConversation();
    this.tab = 'orders';
    this.startEdit(o);
  }

  /** What was said since the last look, and the reply being written now, every 2 s. */
  private async pollConversation(): Promise<void> {
    const view = this.viewing;
    if (!view || this._gone) return;
    try {
      const after = view.lastId ? `&after=${encodeURIComponent(view.lastId)}` : '';
      const r = await lastValueFrom(this.http.get<any>(
        this.mux(`/trusts/conversations/${encodeURIComponent(view.row.channelId)}?${this.teamQ}${after}`)));
      if (this.viewing !== view) return;
      const fresh = (r?.messages || []).map((m: any) => this.toTurn(m, view.row));
      if (!r?.partial) { view.stored = fresh; view.hasMore = !!r?.has_more; }
      else if (fresh.length) view.stored = [...view.stored, ...fresh];
      const last = (r?.messages || []).slice(-1)[0];
      if (last?.id) view.lastId = last.id;
      const lastReply = [...(r?.messages || [])].reverse().find((m: any) => m.role === 'agent');
      if (lastReply?.id) view.lastReplyId = lastReply.id;
      const writing = r?.streaming?.content;
      view.live = !!r?.streaming;
      view.messages = view.live
        ? [...view.stored, { isSelf: false, content: writing || '', agentName: view.row.agent,
                             timestamp: new Date().toISOString(), isStreaming: true, isFinal: false }]
        : view.stored;
      view.loaded = true; view.error = '';
    } catch (e: any) {
      if (this.viewing !== view) return;
      view.error = e?.error?.detail || 'Could not read the conversation. Trying again.';
      if (e?.status === 404) return;
    }
    if (this.viewing === view) this._viewTimer = setTimeout(() => void this.pollConversation(), 2000);
  }

  // ---- feedback: the host's verdict goes to the agent, never to the guest ---------------

  startFeedback(): void {
    if (!this.viewing) return;
    this.viewing.feedback = { mark: '', text: '', aboutLatest: !!this.viewing.lastReplyId, sending: false };
    this.viewing.feedbackNote = '';
  }

  cancelFeedback(): void {
    if (this.viewing) this.viewing.feedback = null;
  }

  feedbackLabel(mark: 'same' | 'partly' | 'no'): string {
    return { same: "I'd have said the same", partly: 'Partly', no: 'Not this' }[mark];
  }

  async sendFeedback(): Promise<void> {
    const view = this.viewing;
    const f = view?.feedback;
    if (!view || !f || !f.mark || f.sending) return;
    f.sending = true; this.error = '';
    try {
      const body: any = { channel_id: view.row.channelId, mark: f.mark, text: f.text.trim() };
      if (f.aboutLatest && view.lastReplyId) body.message_id = view.lastReplyId;
      const r = await lastValueFrom(this.http.post<any>(this.mux(`/trusts/feedback?${this.teamQ}`), body));
      view.feedback = null;
      view.row.feedback = { ...view.row.feedback, [f.mark]: (view.row.feedback[f.mark] || 0) + 1 };
      view.feedbackNote = `Sent to ${view.row.agent} in your channel "Feedback for ${view.row.agent}". It will answer you there.`;
      void this.pollActivity();
      void r;
    } catch (e) {
      this.fail(e, 'Could not send the feedback.');
      f.sending = false;
    }
  }

  feedbackSummary(fb: { same: number; partly: number; no: number } | undefined): string {
    if (!fb) return '';
    const parts = [];
    if (fb.same) parts.push(`${fb.same} same`);
    if (fb.partly) parts.push(`${fb.partly} partly`);
    if (fb.no) parts.push(`${fb.no} no`);
    return parts.join(' · ');
  }

  /** A stored message as the chat screen draws it. No message id is passed on purpose:
   *  pin, react and delete belong to a channel I work in, and this view is read-only. */
  private toTurn(m: any, row: ActivityRow): any {
    if (m.role === 'guest') {
      const content = String(m.content || '').replace(/<context>[\s\S]*?<\/context>\s*/g, '').trim();
      return { isSelf: true, content, agentName: m.from || row.guest, timestamp: m.timestamp, isFinal: true };
    }
    const { content, links } = ChatScreenComponent.extractLinks(m.content || '');
    return {
      isSelf: false, content, agentName: row.agent, timestamp: m.timestamp, isFinal: true,
      ...(links.length ? { links } : {}), ...(m.model ? { model: m.model } : {}),
      ...(m.cancelled ? { isCancelled: true } : {}),
      // the guest's reactions are the guest's own signal about this reply
      ...(m.reactions?.length ? { reactions: m.reactions } : {}),
    };
  }

  /** My agents' spend this month, by the person who asked. */
  private buildUsageByGuest(): UsageRow[] {
    const by = new Map<string, { guest: string; turns: number; cost: number; agents: Set<string> }>();
    for (const o of this.orders) {
      for (const g of o.guests) {
        const row = by.get(g.username) || { guest: g.username, turns: 0, cost: 0, agents: new Set<string>() };
        row.turns += g.turns || 0;
        row.cost += g.cost_usd || 0;
        row.agents.add(o.agent_name);
        by.set(g.username, row);
      }
    }
    return Array.from(by.values())
      .map((r) => ({ guest: r.guest, turns: r.turns, cost: r.cost, agents: Array.from(r.agents).join(', ') }))
      .sort((a, b) => b.cost - a.cost || b.turns - a.turns);
  }

  ago(ts?: number | null): string {
    if (!ts) return 'never';
    const s = Math.max(0, Date.now() / 1000 - ts);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
    if (s < 172800) return 'yesterday';
    return new Date(ts * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}
